import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, ClipboardList, Calendar, MessageSquare, Trash2, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api, formatDateTimeBR, ROLES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS_OPTIONS = [
  { value: "em_dia", label: "Em dia", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { value: "aguardando", label: "Aguardando providência", icon: Clock, cls: "bg-amber-50 text-amber-900 border-amber-200" },
  { value: "em_atraso", label: "Em atraso", icon: AlertCircle, cls: "bg-red-50 text-red-800 border-red-200" },
  { value: "concluida", label: "Concluída", icon: CheckCircle2, cls: "bg-slate-100 text-slate-700 border-slate-200" },
];

export const ProvidenciasPanel = ({ contractId }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ description: "", status: "em_dia" });

  const canEdit = user.role === ROLES.SECRETARIA || user.role === ROLES.DIRETORA;

  const load = async () => {
    try {
      const { data } = await api.get("/providencias", { params: { contract_id: contractId } });
      setItems(data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [contractId]);

  const create = async () => {
    if (!form.description.trim()) return toast.error("Descrição obrigatória.");
    try {
      await api.post("/providencias", { contract_id: contractId, ...form });
      toast.success("Providência registrada.");
      setOpen(false);
      setForm({ description: "", status: "em_dia" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/providencias/${id}`, { status });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta providência?")) return;
    try {
      await api.delete(`/providencias/${id}`);
      load();
    } catch {}
  };

  return (
    <Card className="card-soft">
      <CardHeader className="flex flex-row justify-between items-center">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-midnight" />
          Providências administrativas (Secretaria)
        </CardTitle>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-midnight hover:bg-ink text-white" data-testid="new-providencia-button">
                <Plus className="w-4 h-4 mr-1" /> Nova providência
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Registrar providência</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Descrição</Label>
                  <Textarea rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Ex.: Tentei contato por ligação e o cliente não respondeu."
                    data-testid="providencia-description" />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={create} className="bg-midnight hover:bg-ink text-white" data-testid="save-providencia-button">Salvar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-24" /> : items.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">Sem providências registradas.</div>
        ) : (
          <ul className="space-y-2">
            {items.map(p => {
              const s = STATUS_OPTIONS.find(o => o.value === p.status) || STATUS_OPTIONS[0];
              return (
                <li key={p.id} className="p-3 border border-slate-200 rounded-md">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900">{p.description}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {p.created_by_name} · {formatDateTimeBR(p.created_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${s.cls}`}>
                        <s.icon className="w-3 h-3" /> {s.label}
                      </span>
                      {canEdit && (
                        <Select value={p.status} onValueChange={(v) => updateStatus(p.id, v)}>
                          <SelectTrigger className="h-7 w-7 p-0">
                            <span className="sr-only">Mudar status</span>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                      {canEdit && (
                        <button onClick={() => remove(p.id)} className="text-red-600 hover:text-red-800" title="Remover">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default function ProvidenciasPage() {
  // Standalone page that lists ALL providencias across contracts for the secretária
  const [items, setItems] = useState(null);
  const [contracts, setContracts] = useState({});

  useEffect(() => {
    api.get("/providencias").then(({ data }) => setItems(data));
    api.get("/contracts").then(({ data }) => {
      const map = {};
      data.forEach(c => { map[c.id] = c; });
      setContracts(map);
    });
  }, []);

  if (!items) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Providências Administrativas</h1>
        <p className="text-sm text-slate-600 mt-1">
          Histórico geral das ações administrativas tomadas pela Secretaria por contrato.
        </p>
      </div>
      <Card className="card-soft">
        <CardContent className="pt-6">
          {items.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Nenhuma providência registrada.</div>
          ) : (
            <ul className="space-y-2">
              {items.map(p => {
                const s = STATUS_OPTIONS.find(o => o.value === p.status) || STATUS_OPTIONS[0];
                const c = contracts[p.contract_id];
                return (
                  <li key={p.id} className="p-3 border border-slate-200 rounded-md">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-500">{c?.client_name} · {c?.product_name}</div>
                        <div className="text-sm text-slate-900 mt-0.5">{p.description}</div>
                        <div className="text-xs text-slate-500 mt-1">{p.created_by_name} · {formatDateTimeBR(p.created_at)}</div>
                      </div>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${s.cls}`}>
                        <s.icon className="w-3 h-3" /> {s.label}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
